import type { ResolveLocationResult, ResolvedMunicipality } from '@buzzmap/shared';

/**
 * One GPS path (T011-FE, canonical territories plan).
 *
 * Every browser acquisition in the public app goes through `acquireGpsFix`
 * (accuracy always included) and every acquisition that must become a saved
 * geography goes through `placeByGps`: acquire → `POST /api/cities/resolve-location`
 * (T005 containment) → on an UNCERTAIN match exactly one high-accuracy retry →
 * manual choice. Raw coordinates are used for the request lifetime only: they
 * are never written to a preference store, a URL or a log line (plan l.77).
 *
 * This module is a leaf (no React, no context) so `GeolocationContext`,
 * the v2 preference adapter and tests can share the single path.
 */

/** A browser fix. `accuracyMeters` is mandatory — a fix without it is not a fix. */
export interface GpsFix {
  lat: number;
  lng: number;
  accuracyMeters: number;
}

export type GpsAcquireError = 'denied' | 'timeout' | 'unavailable';

export type GpsAcquireResult =
  | { kind: 'ok'; fix: GpsFix }
  | { kind: 'error'; errorType: GpsAcquireError };

/** Fast attempt: low accuracy, short timeout, allow a recent cached position. */
const FAST_OPTIONS: PositionOptions = {
  enableHighAccuracy: false,
  timeout: 5000,
  maximumAge: 300000, // 5 minutes
};

/** Accurate attempt: high accuracy, long timeout, no cache. */
const ACCURATE_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 30000,
  maximumAge: 0,
};

function mapGeolocationError(err: GeolocationPositionError): GpsAcquireError {
  switch (err.code) {
    case err.PERMISSION_DENIED:
      return 'denied';
    case err.POSITION_UNAVAILABLE:
      return 'unavailable';
    case err.TIMEOUT:
      return 'timeout';
    default:
      return 'unavailable';
  }
}

/**
 * Acquire one position fix. Always resolves (never throws) and always carries
 * the browser-reported accuracy.
 */
export function acquireGpsFix(quality: 'fast' | 'high'): Promise<GpsAcquireResult> {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
      resolve({ kind: 'error', errorType: 'unavailable' });
      return;
    }
    const options = quality === 'high' ? ACCURATE_OPTIONS : FAST_OPTIONS;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          kind: 'ok',
          fix: {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracyMeters:
              typeof position.coords.accuracy === 'number' && Number.isFinite(position.coords.accuracy)
                ? position.coords.accuracy
                : 0,
          },
        });
      },
      (err) => resolve({ kind: 'error', errorType: mapGeolocationError(err) }),
      options
    );
  });
}

/** Runtime guard for a `resolve-location` body (never trust a network body). */
function isResolveLocationResult(v: unknown): v is ResolveLocationResult {
  if (!v || typeof v !== 'object') return false;
  const kind = (v as { kind?: unknown }).kind;
  return kind === 'resolved' || kind === 'ambiguous' || kind === 'unavailable';
}

/** Bound the containment fetch: a hang must not hold the v2 entry gate forever (S5). */
export const RESOLVE_LOCATION_TIMEOUT_MS = 12000;

/**
 * One-time point containment through the T005 endpoint.
 * `accuracyMeters: 0` marks a saved-snapshot conversion (unique point coverage,
 * no claim of a current accurate fix — plan l.61); a real fix passes its own
 * accuracy so an envelope crossing a boundary comes back `ambiguous`.
 * Coordinates never appear in the URL.
 */
export function resolvePoint(lat: number, lng: number, accuracyMeters = 0): Promise<ResolveLocationResult> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), RESOLVE_LOCATION_TIMEOUT_MS);
  return fetch('/api/cities/resolve-location', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ lat, lng, accuracyMeters }),
    signal: controller.signal,
  })
    .then(async (r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const body: unknown = await r.json();
      if (!isResolveLocationResult(body)) throw new Error('malformed resolve-location body');
      return body;
    })
    .finally(() => clearTimeout(timer));
}

/**
 * Outcome of the one GPS placement path.
 * - `resolved`: a unique containment → apply the comune.
 * - `ambiguous`: still uncertain after the single high-accuracy retry (or the
 *   candidates from the first attempt when the retry could not run) → MANUAL
 *   CHOICE, never a nearest/guess.
 * - `unavailable`: outside the registry / no containing comune → manual choice.
 * - `error`: the browser itself failed (denied/timeout) or the endpoint was
 *   unreachable — the caller decides (a transient fault must not be persisted
 *   as a terminal outcome).
 */
export type GpsPlacementOutcome =
  | { kind: 'resolved'; municipality: ResolvedMunicipality }
  | { kind: 'ambiguous'; candidates: ResolvedMunicipality[] }
  | { kind: 'unavailable' }
  | { kind: 'error'; reason: GpsAcquireError | 'network' };

/**
 * Contain one fix; `null` means the endpoint was unreachable. Shared by both
 * containment attempts so the network-failure outcome stays identical.
 */
async function containFix(fix: GpsFix): Promise<ResolveLocationResult | null> {
  try {
    return await resolvePoint(fix.lat, fix.lng, fix.accuracyMeters);
  } catch {
    return null;
  }
}

/**
 * Acquire → contain → retry once with high accuracy when the match is
 * uncertain → manual choice. Pass an existing fix (from
 * `GeolocationContext.requestLocation`, which now always carries accuracy) to
 * skip the acquisition.
 */
export async function placeByGps(fix?: GpsFix): Promise<GpsPlacementOutcome> {
  let attempt = fix ? { kind: 'ok' as const, fix } : await acquireGpsFix('fast');
  if (attempt.kind === 'error') return { kind: 'error', reason: attempt.errorType };

  const first = await containFix(attempt.fix);
  if (!first) return { kind: 'error', reason: 'network' };
  if (first.kind === 'resolved') return { kind: 'resolved', municipality: first.municipality };
  if (first.kind === 'unavailable') return { kind: 'unavailable' };

  // Uncertain spatial match → ONE high-accuracy retry, then manual choice.
  attempt = await acquireGpsFix('high');
  if (attempt.kind === 'error') return { kind: 'ambiguous', candidates: first.candidates };
  const second = await containFix(attempt.fix);
  if (!second) return { kind: 'error', reason: 'network' };
  if (second.kind === 'resolved') return { kind: 'resolved', municipality: second.municipality };
  if (second.kind === 'ambiguous') return { kind: 'ambiguous', candidates: second.candidates };
  return { kind: 'unavailable' };
}
